import {Box, HBox} from '@buncha/components/core/view';
import {Dimensions, StyleSheet} from 'react-native';
import React, {Dispatch, SetStateAction, useCallback} from 'react';
import {useColorThemeType, useFontTheme} from '@buncha/appStyles/hooks/theme';

import {Divider} from 'native-base';
import {H6Text} from '@buncha/components/core/text';
import {HomeScreens} from '@buncha/home/navigation/types';
import If from '@buncha/components/conditional/If';
import {Modules} from '@buncha/utils/types';
import RoundedTickIcon from '@buncha/components/core/icon/RoundedTickIcon';
import {Touchable} from '@buncha/components/core/touchable';
import {useGetLocale} from '@buncha/localization/hooks';

interface Props {
  failureReason?: string;
  toggleSheet: () => void;
  setReason: Dispatch<SetStateAction<string>>;
  otherReason: string;
}

const TaskCompletionCell = (props: Props) => {
  const {failureReason, setReason, toggleSheet, otherReason} = props;
  const muli = useFontTheme('muli');
  const theme = useColorThemeType();
  const {title, body} = useGetLocale(Modules.driver, HomeScreens.Driver) ?? {};

  const clearFailure = useCallback(() => {
    setReason('');
  }, [setReason]);

  return (
    <Box style={styles.mainContainer}>
      <H6Text
        style={{
          color: theme?.text?.blue?.[300],
          fontFamily: muli?.[700]?.normal,
        }}>
        {title?.taskStatus}
      </H6Text>
      <Touchable onPress={clearFailure}>
        <HBox style={styles.container}>
          <HBox style={styles.box}>
            <Box
              style={[
                styles.circle,
                {
                  backgroundColor: theme?.icon.green[100],
                },
              ]}
            />
            <H6Text
              style={{
                color: failureReason
                  ? theme?.text.blue[300]
                  : theme?.text?.green?.[100],
              }}>
              {body?.success}
            </H6Text>
          </HBox>
          <If condition={!failureReason}>
            <RoundedTickIcon
              height={22}
              viewBox="0 0 24 24"
              width={22}
              fill={theme?.icon?.blue?.[300]}
            />
          </If>
        </HBox>
      </Touchable>
      <Touchable onPress={toggleSheet}>
        <HBox style={styles.container}>
          <HBox style={styles.box}>
            <Box
              style={[
                styles.circle,
                {
                  backgroundColor: theme?.icon.red[100],
                },
              ]}
            />
            <H6Text
              style={{
                color: failureReason
                  ? theme?.text?.red?.[100]
                  : theme?.text.blue[300],
              }}>
              {body?.failure}
              {failureReason?.trim() ? ':' : ''}
            </H6Text>
            <If condition={failureReason?.trim()}>
              <H6Text
                style={[
                  {
                    borderColor: theme?.background.gray[200],
                    color: failureReason
                      ? theme?.text?.red?.[100]
                      : theme?.text.blue[300],
                  },
                ]}>
                {failureReason}
              </H6Text>
            </If>
          </HBox>
          <If condition={failureReason}>
            <RoundedTickIcon
              height={22}
              viewBox="0 0 24 24"
              width={22}
              fill={theme?.icon?.blue?.[300]}
            />
          </If>
        </HBox>
      </Touchable>
      <If condition={otherReason?.trim()}>
        <Touchable style={styles.failureBox} onPress={toggleSheet}>
          <H6Text
            numberOfLines={3}
            style={[
              styles.failureText,
              {
                borderColor: theme?.background.gray[200],
                color: otherReason
                  ? theme?.text?.red?.[100]
                  : theme?.text.blue[300],
              },
            ]}>
            {otherReason}
          </H6Text>
        </Touchable>
      </If>
      <Divider />
    </Box>
  );
};

export default React.memo(TaskCompletionCell);

const styles = StyleSheet.create({
  mainContainer: {
    alignItems: 'flex-start',
    marginTop: 10,
  },
  divider: {
    marginLeft: 15,
    width: Dimensions.get('screen').width - 60,
  },
  container: {
    justifyContent: 'space-between',
    alignItems: 'center',
    width: Dimensions.get('screen').width - 40,
    paddingVertical: 5,
  },
  circle: {
    height: 10,
    width: 10,
    borderRadius: 10,
  },
  box: {
    gap: 10,
    maxWidth: '90%',
  },
  failureText: {
    borderWidth: 2,
    borderRadius: 10,
    textAlign: 'left',
    width: '100%',
    padding: 5,
    marginBottom: 10,
    marginTop: 5,
    height: 70,
  },
  failureBox: {
    width: '100%',
  },
});
